import React, { useState, useEffect } from 'react';
import { Shield } from 'lucide-react';
import CompanySettings from '../components/settings/CompanySettings';
import FiscalSettings from '../components/settings/FiscalSettings';
import SalesSettings from '../components/settings/SalesSettings';
import PaymentSettings from '../components/settings/PaymentSettings';
import SecuritySettings from '../components/settings/SecuritySettings';
import { db } from '../lib/supabase';
import type { SystemSettings } from '../types/settings';

type SettingsTab = 'company' | 'fiscal' | 'sales' | 'payment' | 'security';

const Settings: React.FC = () => {
  const [currentTab, setCurrentTab] = useState<SettingsTab>('company');
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState<SystemSettings | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const companyData = await db.companySettings.get();
      
      setSettings({
        company: companyData || {
          name: '',
          document: '',
          phone: '',
          email: '',
          socialMedia: {}
        },
        fiscal: {
          companyType: 'Simples',
          taxRegime: 'Simples Nacional',
          defaultCFOP: {
            sale: '5.102',
            return: '1.202',
            bonus: '5.910'
          },
          allowWeekendInvoices: false,
          requireNCMValidation: true,
          requireCSOSNValidation: true
        },
        sales: {
          allowAnonymousSale: true,
          maxDiscount: {
            percentage: 10,
            amount: 1000
          },
          blockSaleWithoutStock: true,
          roundingMethod: 'nearest',
          defaultPrinter: 'thermal'
        },
        payment: {
          defaultDueDays: 30,
          lateFees: {
            percentage: 2,
            amount: 0
          },
          blockCustomerAfterDays: 15,
          recurringBillsEnabled: true
        },
        security: {
          sessionTimeout: 30,
          allowMultipleLogins: false,
          twoFactorEnabled: false,
          maxLoginAttempts: 3,
          passwordPolicy: {
            minLength: 8,
            requireNumbers: true,
            requireSpecialChars: true,
            requireUppercase: true,
            expirationDays: 90
          }
        },
        language: 'pt-BR',
        timezone: 'America/Sao_Paulo',
        dateFormat: 'dd/MM/yyyy',
        decimalPlaces: 2,
        updatedAt: new Date(),
        updatedBy: 'Admin'
      });
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (data: Partial<SystemSettings>) => {
    setIsLoading(true);
    try {
      if (data.company) {
        await db.companySettings.update(data.company);
      }
      
      // Update local state
      if (settings) {
        setSettings({
          ...settings,
          ...data,
          updatedAt: new Date()
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = () => {
    if (!settings) return;
    
    const dataStr = JSON.stringify(settings, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'system-settings.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">Erro ao carregar configurações</p>
          <button
            onClick={loadSettings}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Shield className="h-8 w-8 text-blue-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">
              Configurações Avançadas
            </h1>
          </div>
          <div className="flex space-x-4">
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Exportar Configurações
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setCurrentTab('company')}
                className={`py-4 px-6 text-sm font-medium ${
                  currentTab === 'company'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Dados da Empresa
              </button>
              <button
                onClick={() => setCurrentTab('fiscal')}
                className={`py-4 px-6 text-sm font-medium ${
                  currentTab === 'fiscal'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Fiscal
              </button>
              <button
                onClick={() => setCurrentTab('sales')}
                className={`py-4 px-6 text-sm font-medium ${
                  currentTab === 'sales'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Vendas
              </button>
              <button
                onClick={() => setCurrentTab('payment')}
                className={`py-4 px-6 text-sm font-medium ${
                  currentTab === 'payment'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Prazos e Pagamentos
              </button>
              <button
                onClick={() => setCurrentTab('security')}
                className={`py-4 px-6 text-sm font-medium ${
                  currentTab === 'security'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Segurança
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="p-6">
            {currentTab === 'company' && (
              <CompanySettings
                settings={settings.company}
                onSave={(data) => handleSave({ company: data })}
                isLoading={isLoading}
              />
            )}
            {currentTab === 'fiscal' && (
              <FiscalSettings
                settings={settings.fiscal}
                onSave={(data) => handleSave({ fiscal: data })}
                isLoading={isLoading}
              />
            )}
            {currentTab === 'sales' && (
              <SalesSettings
                settings={settings.sales}
                onSave={(data) => handleSave({ sales: data })}
                isLoading={isLoading}
              />
            )}
            {currentTab === 'payment' && (
              <PaymentSettings
                settings={settings.payment}
                onSave={(data) => handleSave({ payment: data })}
                isLoading={isLoading}
              />
            )}
            {currentTab === 'security' && (
              <SecuritySettings
                settings={settings.security}
                onSave={(data) => handleSave({ security: data })}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;